import {initInScope, cleanUpInScope, onFind} from "@elements/init-modules-in-scope";
import {getPrefixedDataSet} from "@elements/data-set-utils";
// import {onEnterViewPort} from "@elements/viewport-utils";
import OSB_MasonryGrid from '@offensichtbar-codestock/es6-flex-masonry-grid';
import 'url-polyfill';
import 'url-search-params-polyfill'; // Edge Polyfill
import {debounce} from "debounce";
import formDataEntries from 'form-data-entries';
import {findAllIn, findIn, on, off, trigger, triggerWith, setAttribute, removeAttribute, closest, append, prepend} from "@elements/dom-utils";
import axios from "axios";

const defaultSelectors = {
    base: '.js-infinite-grid',
    result: '.js-infinite-grid__result',
    resultContent: '.js-infinite-grid__result-content',
    loading: '.js-infinite-grid__loading',
    form: '.js-infinite-grid__form',
    additionalForm: '.js-infinite-grid__additional-form',
    retry: '.js-infinite-grid__retry',
    loadMore: '.js-infinite-grid__load-more',
    gridItem: '.js-infinite-grid__item'
};

const defaultOptions = {
    submitOnChange: false,
    addUrlParams: true,
    submitOnReset: false,
    fetchHeaders: {},
    allowScriptTags: false,
    validator: () => true,
    onSubmit: () => {}
};

export function init(options = defaultOptions, selectors = defaultSelectors) {
    onFind(selectors.base, function (baseElement) {
        createInfinitGrid(
            baseElement,
            {...defaultOptions, ...options},
            {...defaultSelectors, ...selectors}
        );
    });
}

export function createInfinitGrid(baseElement, options = defaultOptions, selectors = defaultSelectors) {
    let osb;
    let pendingRequest = null;
    let form = findIn(selectors.form, baseElement);
    let currentPage = parseInt(new URL(location.href).searchParams.get('page')) || 1;
    let visibleRange = [currentPage, currentPage];

    options = {
        ...defaultOptions,
        ...options,
        ...transformDataSetOptions(getPrefixedDataSet('ajax-form', baseElement))
    };

    if (matchMedia('(min-width: 768px)').matches) {
        onFind(selectors.resultContent, function (contentElement) {
            osb = new OSB_MasonryGrid(findIn(selectors.resultContent, baseElement), {
                animation: true,
                // animationType: 'fade',
                animationDelay: 0,
                observeDOM: true
            });
        });
    }

    // form
    onFind(selectors.form, function (form) {
        const submitHandler = evt => {
            evt.preventDefault();
            evt.stopImmediatePropagation(); // otherwise .on('submit.ajax-form') would be called twice
            submitForm(form, evt);
        };

        const changeHandler = debounce(() => {
            submitForm(form);
        }, 200);

        on('submit', submitHandler, form);

        if (options.submitOnChange) {
            on('change', changeHandler, form);
        }

        return () => {
            off('submit', submitHandler, form);
            off('change', changeHandler, form);
        }
    }, baseElement);

    onFind(selectors.loadMore, (link) => {
        let pageLink = link.getAttribute('data-load-more-type') === 'append' ? currentPage + 1 : currentPage - 1;
        // setAttribute('href', '?page=' + pageLink + '', link);
        setAttribute('data-page', pageLink, link);

        function linkClickHandler(evt) {
            evt.preventDefault();

            let closestForm = closest('form', link);

            let action = closestForm ? getActionByForm(closestForm) : form.dataset.action || form.getAttribute('action');
            let formDataEntries = getFormDataEntries();
            let params = new URLSearchParams(formDataEntries);
            params.append('page', link.getAttribute('data-page'));

            let url = new URL(location.href);
            url = addSearchParamsToUrl(url, params);

            pendingRequest = load(action, 'GET', params, url, link.getAttribute('data-load-more-type'), osb);

            pendingRequest
                .then(() => pendingRequest = null)
                .catch((error) => {
                    if ((!error || error.name !== 'AbortError')) {
                        pendingRequest = null;
                    }
                });
        }

        on('click', linkClickHandler, link);

        return () => {
            off('click', linkClickHandler, link);
        }
    }, baseElement);

    let lastLoadParams = null; // for retry
    function load(url, method, params, historyUrl, loadMoreType, osb) {
        method = method || "GET";
        lastLoadParams = [url, method, params, historyUrl];

        let baseUrl = url;

        if (options.addUrlParams) {
            history.replaceState(history.state, document.title, historyUrl || url);
        }

        // add base url to params (path)
        params.append('baseUrl', location.pathname);

        if (method.toUpperCase() === "GET") {
            params.append('ajax', 1);
            // Add ajax param to differentiate between and ajax requests and page request.
            // Otherwise Chrome caches these results as normal pages and returns them from cache if the back button is pressed
            url = addSearchParamsToUrl(url, params);
        }

        removeAttribute('hidden', findIn(selectors.loading, baseElement));

        let request = axios({
            method: method,
            url: baseUrl,
            headers: {
                'pragma': 'no-cache',
                'cache-control': 'no-cache',
                ...options.fetchHeaders
            },
            ...(method.toUpperCase() === "GET" ? {
                params: new URLSearchParams(params)
            } : {
                data: new URLSearchParams(params)
            })
        });

        triggerWith('fetch.ajax-form', request, baseElement);

        request.then(({data}) => {
            if (data.success) {
                if (loadMoreType) {
                    if (loadMoreType === 'append') {
                        append(data.content, {}, findIn(selectors.resultContent, baseElement));
                    } else {
                        prepend(data.content, {}, findIn(selectors.resultContent, baseElement));
                    }

                    cleanUpInScope(findIn(selectors.resultContent, baseElement).parentNode)
                    initInScope(findIn(selectors.resultContent, baseElement).parentNode)
                } else {
                    findIn(selectors.result, baseElement).innerHTML = data.content;

                    form = findIn(selectors.form, baseElement);

                    cleanUpInScope(findIn(selectors.result, baseElement))
                    initInScope(findIn(selectors.result, baseElement))
                }

                setAttribute('hidden', true, findIn(selectors.loading, baseElement));

                if (osb) {
                    osb._initCards();
                    osb._animateCards();
                }

                if (loadMoreType === 'append') {
                    visibleRange[1] = visibleRange[1] + 1;
                } else {
                    visibleRange[0] = visibleRange[0] - 1;
                }

                setButtons(baseElement, visibleRange, data.totalPages);
            }
        });

        return request;
    }

    function submitForm(form, evt) {
        let submitBtn;
        let action;

        if(evt !== undefined && evt.submitter){
            submitBtn = evt.submitter;
            if(submitBtn.getAttribute('formaction')){
                action = submitBtn.getAttribute('formaction');
            }
        }else{
            action = getActionByForm(form);
        }

        return submit(
            action,
            form.dataset.method || form.getAttribute('method'),
            submitBtn
        )
    }

    function submit(action, method, submitBtn) {
        action = action || getActionByForm(form);
        method = method || form.dataset.method || form.getAttribute('method');

        // create promise to resolve/reject in right order (important for loading-indicator with multiple submissions)
        let readyToSubmit = new Promise(function (resolve, reject) {
            if (pendingRequest && pendingRequest.abort) {
                pendingRequest.abort();
                pendingRequest.catch(resolve);
                pendingRequest = null;
            } else {
                resolve();
            }
        });

        readyToSubmit.then(function () {
            if (typeof options.validator === "function" && !options.validator({
                baseElement,
                form
            })) {
                return;
            }

            trigger('submit.ajax-form', baseElement);

            action = action || getActionByForm(form);
            method = method || form.dataset.method || form.getAttribute('method');
            let formDataEntries = getFormDataEntries();
            let params = new URLSearchParams(formDataEntries);

            options.onSubmit({
                element: baseElement,
                options,
                formData: getFormData()
            });

            let url = new URL(location.href);
            url.searchParams.delete('page');
            url = addSearchParamsToUrl(url, params);

            visibleRange = [1, 1];
            pendingRequest = load(action, method, params, url);

            pendingRequest
                .then(() => pendingRequest = null)
                .catch((error) => {
                    if (!error || error.name !== 'AbortError') {
                        pendingRequest = null;
                    }
                });
        });
    }

    function getFormData() {
        return createFormData([form, ...findAllIn(selectors.additionalForm, baseElement)]);
    }

    function getFormDataEntries() {
        return createFormDataEntries([form, ...findAllIn(selectors.additionalForm, baseElement)]);
    }
}

function addSearchParamsToUrl(url, searchParams) {
    url = new URL(url, location.origin);

    let searchParamsArray = Array.from(searchParams);
    searchParamsArray.forEach(([name]) => url.searchParams.delete(name));
    searchParamsArray.forEach(([name, value]) => url.searchParams.append(name, value));

    return url;
}

function createFormData(forms) {
    let formData = new FormData();
    forms.map(form => {
        for (var pair of formDataEntries(form)) {
            formData.append(...pair);
        }
    });

    return formData;
}

function createFormDataEntries(forms) {
    let formDataArray = [];

    forms.map(form => {
        // unchecked checkboxes and radios needs to be added manually to formDataArray
        let selectors = findAllIn('input[type="radio"], input[type="checkbox"]', form);
        let selectorNames = [];

        // selects needs to be added manually to formDataArray
        let selects = form.getElementsByTagName("SELECT");

        selectors = [...selectors, ...selects];

        selectors.map(function(selector) {
            selectorNames.push(selector.name);
        });

        for (var pair of formDataEntries(form)) {
            formDataArray.push(pair);
        }

        let existingNames = formDataArray.map(entry => entry[0]);
        selectorNames = [...new Set(selectorNames)];

        selectorNames.forEach(function (name) {
            let newEntry = [name, ""];
            if(!existingNames.includes(name)) {
                formDataArray.push(newEntry);
            } else {
                formDataArray.filter(item => item !== newEntry);
            }
        });
    });

    return formDataArray;
}

function transformDataSetOptions(options = {}) {
    let transformedOptions = {...options};

    if (transformedOptions.fetchHeaders) {
        try {
            transformedOptions.fetchHeaders = JSON.parse(transformedOptions.fetchHeaders)
        } catch (e) {
            transformedOptions.fetchHeaders = {};
        }
    }

    if (transformedOptions.allowScriptTags) {
        transformedOptions.allowScriptTags = !!(transformedOptions.allowScriptTags === "true" || transformedOptions.allowScriptTags === true)
    }

    return transformedOptions;
}

function getActionByForm(form) {
    return form.dataset.action || form.getAttribute('action') || location.pathname
}

function setButtons(baseElement, visibleRange, totalPages) {
    if (findIn('.js-infinite-grid__load-more[data-load-more-type="append"]', baseElement)) {
        if (visibleRange[1] < totalPages) {
            removeAttribute('hidden', findIn('.js-infinite-grid__load-more[data-load-more-type="append"]', baseElement));
            setAttribute('data-page', (visibleRange[1] + 1), findIn('.js-infinite-grid__load-more[data-load-more-type="append"]', baseElement));
        } else {
            setAttribute('hidden', true, findIn('.js-infinite-grid__load-more[data-load-more-type="append"]', baseElement));
        }
    }

    if (findIn('.js-infinite-grid__load-more[data-load-more-type="prepend"]', baseElement)) {
        if (visibleRange[0] > 1) {
            removeAttribute('hidden', findIn('.js-infinite-grid__load-more[data-load-more-type="prepend"]', baseElement));
            setAttribute('data-page', (visibleRange[0] - 1), findIn('.js-infinite-grid__load-more[data-load-more-type="prepend"]', baseElement));
        } else {
            setAttribute('hidden', true, findIn('.js-infinite-grid__load-more[data-load-more-type="prepend"]', baseElement));
        }
    }
}